// src/pages/TeamDetail.js
import React, { useEffect, useState } from 'react';
import { Container, Card, Row, Col, Nav, Tab, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import useTeamStore from '../store/teamStore';
import useAuthStore from '../store/authStore';
import MemberList from '../components/Teams/MemberList';
import AddMemberForm from '../components/Teams/AddMemberForm';
import WebsiteAssignment from '../components/Teams/WebsiteAssignment';
import TeamForm from '../components/Teams/TeamForm';
import { ConfirmModal } from '../components/UI/Modal';

const TeamDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const {
        currentTeam,
        members,
        teamWebsites,
        fetchTeamDetail,
        updateTeam,
        deleteTeam,
        addTeamMember,
        removeTeamMember,
        assignWebsiteToTeam,
        removeWebsiteFromTeam,
        loading,
        error
    } = useTeamStore();

    const [activeTab, setActiveTab] = useState('members');
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        fetchTeamDetail(id);
    }, [fetchTeamDetail, id]);

    const handleUpdateTeam = async (teamData) => {
        try {
            await updateTeam(id, teamData);
            setIsEditing(false);
        } catch (err) {
            // Chyba je zachycena v store a zobrazena přes Toast
        }
    };

    const handleDeleteTeam = async () => {
        try {
            await deleteTeam(id);
            navigate('/teams');
        } catch (err) {
            // Chyba je zachycena v store a zobrazena přes Toast
        }
    };

    const handleAddMember = async (memberData) => {
        try {
            await addTeamMember(id, memberData);
        } catch (err) {
            // Chyba je zachycena v store a zobrazena přes Toast
        }
    };

    const handleRemoveMember = async (memberId) => {
        try {
            await removeTeamMember(id, memberId);
        } catch (err) {
            // Chyba je zachycena v store a zobrazena přes Toast
        }
    };

    const handleAssignWebsite = async (teamId, websiteId) => {
        try {
            await assignWebsiteToTeam(teamId, websiteId);
        } catch (err) {
            // Chyba je zachycena v store a zobrazena přes Toast
        }
    };

    const handleRemoveWebsite = async (teamId, websiteId) => {
        try {
            await removeWebsiteFromTeam(teamId, websiteId);
        } catch (err) {
            // Chyba je zachycena v store a zobrazena přes Toast
        }
    };

    if (loading && !currentTeam) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání detailu týmu...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <Container fluid>
                <Alert variant="danger">{error}</Alert>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate('/teams')}
                >
                    <i className="fas fa-arrow-left me-2"></i> Zpět na týmy
                </Button>
            </Container>
        );
    }

    if (!currentTeam) {
        return (
            <Container fluid>
                <Alert variant="warning">Tým nebyl nalezen.</Alert>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate('/teams')}
                >
                    <i className="fas fa-arrow-left me-2"></i> Zpět na týmy
                </Button>
            </Container>
        );
    }

    const isOwner = currentTeam.isAdmin;
// src/pages/TeamDetail.js (pokračování)
    return (
        <Container fluid>
            <div className="d-md-flex justify-content-between align-items-start mb-4">
                <div className="mb-3 mb-md-0">
                    {isEditing ? (
                        <h2>Úprava týmu</h2>
                    ) : (
                        <>
                            <h1 className="mb-2">{currentTeam.name}</h1>
                            <div className="d-flex align-items-center">
                                <Badge
                                    bg="primary"
                                    className="me-3 px-3 py-2"
                                >
                                    <i className="fas fa-users me-2"></i>
                                    {members.length} {members.length === 1 ? 'člen' : members.length >= 2 && members.length <= 4 ? 'členové' : 'členů'}
                                </Badge>
                                <Badge
                                    bg="info"
                                    className="px-3 py-2"
                                >
                                    <i className="fas fa-globe me-2"></i>
                                    {teamWebsites.length} {teamWebsites.length === 1 ? 'web' : teamWebsites.length >= 2 && teamWebsites.length <= 4 ? 'weby' : 'webů'}
                                </Badge>
                            </div>
                        </>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/teams')}
                    >
                        <i className="fas fa-arrow-left me-2"></i> Zpět
                    </Button>

                    {isOwner && !isEditing && (
                        <>
                            <Button
                                variant="outline-primary"
                                onClick={() => setIsEditing(true)}
                            >
                                <i className="fas fa-edit me-2"></i> Upravit
                            </Button>
                            <Button
                                variant="outline-danger"
                                onClick={() => setShowDeleteModal(true)}
                            >
                                <i className="fas fa-trash-alt me-2"></i> Smazat
                            </Button>
                        </>
                    )}

                    {isEditing && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setIsEditing(false)}
                        >
                            Zrušit
                        </Button>
                    )}
                </div>
            </div>

            {isEditing ? (
                <Card className="mb-4 shadow-sm">
                    <Card.Body>
                        <TeamForm
                            team={currentTeam}
                            onSubmit={handleUpdateTeam}
                            loading={loading}
                        />
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    <Col lg={8}>
                        <Card className="mb-4 shadow-sm">
                            <Card.Header className="bg-white">
                                <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                                    <Nav.Item>
                                        <Nav.Link eventKey="members">
                                            <i className="fas fa-users me-2"></i> Členové
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="websites">
                                            <i className="fas fa-globe me-2"></i> Weby
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Card.Header>
                            <Card.Body>
                                <Tab.Content>
                                    <Tab.Pane eventKey="members" active={activeTab === 'members'}>
                                        {isOwner && (
                                            <div className="mb-4">
                                                <h5 className="mb-3">Přidat nového člena</h5>
                                                <AddMemberForm
                                                    onSubmit={handleAddMember}
                                                    loading={loading}
                                                />
                                                <p className="text-muted small mt-2">
                                                    <i className="fas fa-info-circle me-1"></i> Uživatel musí být již zaregistrován v systému
                                                </p>
                                            </div>
                                        )}

                                        <h5 className="mb-3">Členové týmu</h5>
                                        <MemberList
                                            members={members}
                                            currentUserId={user?.id}
                                            teamOwnerId={currentTeam.ownerId}
                                            onRemove={isOwner ? handleRemoveMember : null}
                                        />
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="websites" active={activeTab === 'websites'}>
                                        <h5 className="mb-3">Weby týmu</h5>
                                        <WebsiteAssignment
                                            teamId={id}
                                            teamWebsites={teamWebsites}
                                            assignWebsiteToTeam={isOwner ? handleAssignWebsite : null}
                                            onRemove={isOwner ? handleRemoveWebsite : null}
                                            loading={loading}
                                        />
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Informace o týmu</h5>
                            </Card.Header>
                            <Card.Body>
                                <dl>
                                    <dt>Vlastník týmu</dt>
                                    <dd>
                                        {members.find(m => m.id === currentTeam.ownerId)?.email || 'Neznámý'}
                                    </dd>

                                    <dt>Vytvořeno</dt>
                                    <dd>
                                        {new Date(currentTeam.createdAt).toLocaleDateString()}
                                    </dd>

                                    {currentTeam.description && (
                                        <>
                                            <dt>Popis</dt>
                                            <dd>{currentTeam.description}</dd>
                                        </>
                                    )}
                                </dl>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteTeam}
                title="Potvrzení smazání týmu"
                message={`Opravdu chcete smazat tým "${currentTeam.name}"? Tato akce odstraní tým a odebere všechna přiřazení webů a uživatelů. Samotné weby nebudou odstraněny.`}
                confirmText="Ano, smazat tým"
                cancelText="Zrušit"
                variant="danger"
            />
        </Container>
    );
};

export default TeamDetail;